// src/components/pages/message/timecapsule/Step3Review.js
// Filepath: // src/components/pages/message/timecapsule/Step3Review.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  LinearProgress,
  Snackbar,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import IconNextArrow from "../../../../assets/icons/icon-next-arrow.svg";
import IconTimeCapsuleUnlocked from "../../../../assets/icons/icon-time-capsule-unlocked.png";
import IconTimeCapsuleLocked from "../../../../assets/icons/v2/time-capsule-locked.webp";


import usePrivateTimeCapsuleSend from "../../../../hooks/usePrivateTimeCapsuleSend";
import MediaPreview from "./components/MediaPreview";
import SmsInviteDialog from "./components/SmsInviteDialog";
import { auth } from "../../../../firebase";
import {
  buildCapsuleShareText,
  getShareCopyVariant,
} from "../../../../utils/shareCopy";
import useUserProfileStore, {
  selectUserProfile,
} from "../../../../store/useUserProfileStore";

export default function Step3Review({ wizardData, setWizardData, onBack, onNavigationLockChange, onBuryComplete }) {
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    lat,
    lng,
    address,
    subject,
    message,
    mediaFile,
    recipients = [],
    phoneNumbers = [],
    revealDate,
  } = wizardData;

  const formattedRevealDate = revealDate
    ? new Date(revealDate).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    : "";

  const hasUpload = !!mediaFile?.file;

  // Raw (unellipsized) display name; shareCopy sanitizes it before use.
  const senderProfile = useUserProfileStore(selectUserProfile);
  const senderDisplayName =
    senderProfile?.displayName || auth.currentUser?.displayName || "";
  const shareText = `${buildCapsuleShareText({
    senderName: senderDisplayName,
    variant: getShareCopyVariant(wizardData),
  })} `;
  const baseUrl = window.location.origin;
  const shareUrl = `${baseUrl}/messages`;

  const actionBarBottom = useMemo(
    () => "env(safe-area-inset-bottom)",
    []
  );

  const {
    isSending,
    uploadProgress,
    error,
    errorMessage,
    showErrorSnack,
    setShowErrorSnack,
    clearError,
    send,
  } = usePrivateTimeCapsuleSend({ wizardData });

  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsList, setSmsList] = useState([]);
  const [savedCapsuleId, setSavedCapsuleId] = useState(null);

  const launchSms = (numbers) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const recipientsParam = numbers.join(",");
    const encodedBody = encodeURIComponent(`${shareText}Get it here! ${shareUrl}`);
    window.location.href = isIOS
      ? `sms:/open?addresses=${recipientsParam}&body=${encodedBody}`
      : `sms:${recipientsParam}?body=${encodedBody}`;
  };

  /**
   * IMPORTANT:
   * We no longer pass "preview" points into the success dialog.
   * The backend award receipt is the source of truth.
   *
   * SuccessDialog will use receiptId to read:
   *   users/{uid}/award_receipts/{receiptId}.deltaPoints
   */
  const navigateHomeWithSuccess = (capsuleId, capsulePreview = null) => {
    const receiptId = capsuleId ? `tc_drop_${capsuleId}` : null;

    navigate("/", {
      state: {
        center: { lat, lng },
        showSuccess: true,
        sentCapsuleId: capsuleId,
        buriedCapsulePreview: capsulePreview,
        successDialog: {
          type: "capsule_buried",
          title: "Successfully buried!",
          subtitle: "",
          rewardPoints: 0, // force "no reward" mode until receipt arrives
          receiptId, // <- SuccessDialog will resolve true deltaPoints from this
          revealDate: capsulePreview?.revealDate || revealDate || null,
        },
      },
    });
  };

  const handleSend = async () => {
    clearError();

    const result = await send({ status: "sent" });
    if (!result) return;

    setSavedCapsuleId(result.capsuleId);
    if (typeof onBuryComplete === "function") {
      onBuryComplete(result);
    }

    if (result.inviteList?.length) {
      setSmsList(result.inviteList);
      setShowSmsModal(true);
      return;
    }

    navigateHomeWithSuccess(result.capsuleId, result.capsulePreview || null);
  };

  const handleSaveDraft = async () => {
    clearError();
    const result = await send({ status: "draft" });
    if (!result) return;
    if (typeof setWizardData === "function") {
      setWizardData((prev) => ({
        ...prev,
        draftId: result.capsuleId || prev?.draftId || "",
        draftStep: "share",
      }));
    }

    navigate("/messages?tab=1&drafts=1");
  };

  const handleSendSms = () => {
    // Send the SMS first, then navigate with receiptId so SuccessDialog can resolve points.
    if (smsList.length) launchSms(smsList);

    // savedCapsuleId should exist because we only show this modal after save,
    // but guard anyway.
    navigateHomeWithSuccess(savedCapsuleId);
  };

  const displayRecipients = () => {
    const manual = phoneNumbers.map((p) =>
      typeof p === "object" && p.name ? `${p.name} (${p.phone})` : p.phone || p
    );
    const contactList = recipients.map((r) =>
      typeof r === "object" && r.name ? `${r.name} (${r.phone})` : r.phone || r
    );
    const combined = [...manual, ...contactList].filter(Boolean);
    return combined.length ? combined.join(", ") : "No recipients selected";
  };

  const getSendButtonText = () => {
    if (error) return "Retry";
    if (isSending) {
      if (hasUpload && uploadProgress < 100) return "Uploading media";
      return "Burying time capsule";
    }
    return "Bury it!";
  };

  const disabledFg = alpha(theme.palette.common.black, 0.6);

  const sendEndIcon = isSending ? (
    <CircularProgress size={18} sx={{ color: disabledFg }} />
  ) : (
    <Box component="img" src={IconNextArrow} alt="next" sx={{ width: 15, height: 15 }} />
  );

  const showUploadProgress = isSending && hasUpload;
  const isUploading = isSending && hasUpload && uploadProgress < 100;

  // Back hidden during upload, if back is hidden, primary button should be full width
  const showBack = !isUploading;

  useEffect(() => {
    if (typeof onNavigationLockChange !== "function") return undefined;
    onNavigationLockChange(isUploading);
    return () => onNavigationLockChange(false);
  }, [isUploading, onNavigationLockChange]);

  return (
    <Box sx={{ pt: 2, pb: "calc(env(safe-area-inset-bottom) + 110px)" }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        Step 3: Review & Confirm
      </Typography>

      <Typography variant="body1" gutterBottom>
        Almost there! Confirm everything and tap {error ? "Retry" : "Bury it!"}
      </Typography>

      <MediaPreview mediaFile={mediaFile} />

      {showUploadProgress && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>
      )}

      <Typography sx={{ mb: 4, fontWeight: 700 }}>{address}</Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 4 }}>
        <Box
          sx={(theme) => ({
            borderRadius: "999px",
            backgroundColor: theme.colors.blue,
            p: 1,
            pl: 2,
            pr: 2,
            alignSelf: "flex-start",
          })}
        >
          <Typography sx={(theme) => ({ color: theme.colors.white })}>My contacts</Typography>
        </Box>
        <Typography sx={(theme) => ({ color: theme.colors.blue, fontWeight: 700 })}>
          {displayRecipients()}
        </Typography>
      </Box>

      <Typography
        sx={{
          mb: 1,
          overflowWrap: "anywhere", // breaks long unbroken strings nicely
          wordBreak: "break-word",  // fallback for older behavior
        }}
      >
        <strong>Subject:</strong> {subject}
      </Typography>
      <Typography
        sx={{
          mb: 4,
          overflowWrap: "anywhere", // breaks long unbroken strings nicely
          wordBreak: "break-word",  // fallback for older behavior
        }}
      >
        <strong>Message:</strong> {message}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <img
          src={formattedRevealDate ? IconTimeCapsuleLocked : IconTimeCapsuleUnlocked}
          alt="Time Capsule"
          style={{ width: "auto", height: "80px" }}
        />
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {formattedRevealDate
            ? `Reveals when your time capsule unlocks on ${formattedRevealDate}`
            : "Reveals instantly if recipients are nearby or from their inbox"}
        </Typography>
      </Box>

      {/* Fixed bottom action bar (above bottom nav) */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: actionBarBottom,
          zIndex: (theme) => theme.zIndex.appBar,
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderTopColor: "divider",
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", width: "100%" }}>
          {showBack && (
            <Button
              onClick={onBack}
              variant="outlined"
              sx={(theme) => ({
                fontWeight: 700,
                px: 3,
                py: 1.25,
                flex: 1,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
              })}
            >
              Back
            </Button>
          )}
          {showBack && (
            <Button
              variant="outlined"
              onClick={handleSaveDraft}
              disabled={isSending}
              sx={(theme) => ({
                fontWeight: 700,
                px: 3,
                py: 1.25,
                flex: 1,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
              })}
            >
              Save draft
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleSend}
            disabled={isSending}
            endIcon={sendEndIcon}
            sx={(theme) => ({
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              fontWeight: 700,
              px: 3,
              py: 1.25,
              flex: 1,
              width: showBack ? "auto" : "100%",

              // Disabled: dark gray text AND dark gray icon wrapper
              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: disabledFg,
                opacity: 1,
              },
              "&.Mui-disabled .MuiButton-endIcon": {
                color: disabledFg, // affects endIcon wrapper (and anything inheriting)
              },
            })}
          >
            {getSendButtonText()}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={showErrorSnack}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnack(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setShowErrorSnack(false)}
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      <SmsInviteDialog
        open={showSmsModal}
        onClose={() => setShowSmsModal(false)}
        onSend={handleSendSms}
      />
    </Box>
  );
}
